import { SectionHead, CTAButton, Reveal } from "@/components/Shared";
import StorySlideshow from "@/components/StorySlideshow";

const LINES = ["Protect the water's edge.", "Educate the child.", "Prepare the family.", "Mobilize the community."];

const STORY = [
  "Rakim was a fun-loving, adventurous little boy who brought joy to everyone he met. He loved to climb, explore, laugh, play in the water, and experience the world with endless curiosity and excitement. His bright smile and energetic spirit left a lasting impression on family, friends, and all who knew him.",
  "Following the tragic loss of Rakim due to drowning, his mother turned her grief into purpose by creating Swim for Rakim™, a mission dedicated to educating children and parents about water safety, drowning prevention, literacy, and life-saving awareness.",
  "Today, Rakim's legacy lives on through programs designed to help protect children, empower families, and teach the importance of safe environments around water. Through education, storytelling, and community outreach, Swim for Rakim™ seeks to ensure that other families do not experience the same heartbreak.",
  "Rakim's story is one of love, hope, and purpose—a reminder that even the shortest lives can make the greatest impact.",
];

function Photos() {
  return (
    <div className="relative" data-testid="story-photos">
      <img src="/brand/rakim-portrait.webp" alt="Rakim smiling in a plaid shirt holding a basketball" className="w-[78%] rounded-3xl shadow-2xl object-cover aspect-[3/4] rotate-[-2deg]" data-testid="story-photo-portrait" />
      <img src="/brand/rakim-smile.webp" alt="Rakim grinning in a red shirt" className="absolute right-0 bottom-[-8%] w-[46%] rounded-3xl shadow-2xl object-cover aspect-[3/4] ring-8 ring-white rotate-[3deg]" data-testid="story-photo-smile" />
    </div>
  );
}

export default function Story() {
  return (
    <div data-testid="story-page">
      <StorySlideshow />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-2 gap-14 items-start">
        <Reveal><Photos /></Reveal>
        <Reveal delay={0.1}>
          <article className="space-y-5 text-lg leading-relaxed text-slate-700 mt-10 lg:mt-0" data-testid="story-text">
            {STORY.map((p) => <p key={p.slice(0, 20)}>{p}</p>)}
          </article>
          <blockquote className="mt-8 border-l-4 border-[#FF6B35] pl-5" data-testid="story-quote">
            <p className="font-script text-4xl text-[#03045E]">"His life was a gift. His legacy is saving lives."</p>
          </blockquote>
        </Reveal>
      </section>

      <section className="bg-[#03045E] text-white relative grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid md:grid-cols-5 gap-10 items-center">
          <Reveal className="md:col-span-2">
            <img src="/brand/rakim-memorial.webp" alt="Loved ones placing their hands on Rakim Akbari's casket" className="rounded-3xl shadow-2xl w-full max-w-sm mx-auto object-cover aspect-[3/4]" data-testid="story-photo-memorial" />
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-sky-300">In loving memory</p>
            <p className="font-display uppercase text-4xl sm:text-5xl mt-2">Rakim Akbari</p>
            <p className="font-script text-3xl text-sky-200 mt-4">His smile lives on through a safer, stronger, brighter tomorrow.</p>
            <p className="text-sky-100 mt-6 max-w-xl">Swim for Rakim™ brings families, schools, churches, businesses, swim instructors, first responders and volunteers together to build many layers of protection against drowning — one child, one family, one pond and one community at a time.</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <SectionHead eyebrow="Our mission" title="Many layers of protection. One community." />
          <div className="mt-8 space-y-1">{LINES.map((l) => <p key={l} className="font-display uppercase text-2xl text-[#03045E]">{l}</p>)}</div>
          <p className="font-script text-3xl text-[#0077B6] mt-6">“Every Child Deserves the Opportunity to Learn to Swim. Every Family Deserves the Knowledge to Help Keep Them Safe.”™</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <CTAButton to="/programs" testid="story-programs-link">See the six pillars</CTAButton>
            <CTAButton to="/events#walk" variant="light" testid="story-walk-link">The Annual Walk</CTAButton>
          </div>
        </div>
        <img src="/brand/infographic.webp" alt="Swim for Rakim poster — Turning Tragedy Into Action" className="rounded-3xl shadow-xl w-full max-w-md mx-auto" data-testid="story-poster" />
      </section>
    </div>
  );
}
